import { FunctionComponent, useState } from 'react'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import { useTranslation } from 'react-i18next'
import styles from './editOpeningHoursDialog.module.scss'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { useMutation } from '@apollo/client'
import { UPDATE_DAYCARE_OPENING } from '@/modules/API/daycare/daycareHandler'
import Checkbox from '@/components/_custom/Checkbox/Checkbox'
import TimePickerComponent from '@/components/_custom/Picker/timePicker/timePicker'
import moment from 'moment/moment'
import { useAuth } from '@/modules/context/AuthContext'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface editDaycareProfileDialogProps {
    open: boolean
    handleClose: () => void
    daycareId: number
    day: string
    openHours: string | null
    setOpenHours: (value: string | null) => void
    closeHours: string | null
    setCloseHours: (value: string | null) => void
    breakStart: string | null
    setBreakStart: (value: string | null) => void
    breakEnd: string | null
    setBreakEnd: (value: string | null) => void
}

const EditOpeningHours: FunctionComponent<editDaycareProfileDialogProps> = ({
    open,
    handleClose,
    daycareId,
    day,
    openHours,
    setOpenHours,
    closeHours,
    setCloseHours,
    breakStart,
    setBreakStart,
    breakEnd,
    setBreakEnd
}: editDaycareProfileDialogProps) => {
    const { t } = useTranslation()

    const { fetchCurrentDaycare } = useAuth()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [checkOpen, setCheckOpen] = useState<boolean>(openHours !== null && closeHours !== null)
    const [checkBreak, setCheckBreak] = useState<boolean>(breakStart !== null && breakEnd !== null)

    const [openHoursEdit, setOpenHoursEdit] = useState<string | null>(openHours ? openHours : null)
    const [closeHoursEdit, setCloseHoursEdit] = useState<string | null>(closeHours ? closeHours : null)
    const [breakStartEdit, setBreakStartEdit] = useState<string | null>(breakStart ? breakStart : null)
    const [breakEndEdit, setBreakEndEdit] = useState<string | null>(breakEnd ? breakEnd : null)

    const [updateDaycare] = useMutation(UPDATE_DAYCARE_OPENING, {
        onCompleted: () => {
            setOpenHours(openHoursEdit)
            setCloseHours(closeHoursEdit)
            setBreakStart(breakStartEdit)
            setBreakEnd(breakEndEdit)

            handleClose()

            fetchCurrentDaycare()

            showNotificationSuccess(t('daycareOpeningHoursUpdated'))
        },
        onError: () => {
            showNotificationError(t('errorUpdateDaycareOpeningHours'))
        }
    })

    function updateHoursDaycare(): void {
        switch (day) {
            case t('monday'):
                updateDaycare({
                    variables: {
                        daycare_id: daycareId,
                        monOpen: checkOpen ? openHoursEdit : null,
                        monClose: checkOpen ? closeHoursEdit : null,
                        monBreakStart: checkBreak ? breakStartEdit : null,
                        monBreakEnd: checkBreak ? breakEndEdit : null
                    }
                })
                break
            case t('tuesday'):
                updateDaycare({
                    variables: {
                        daycare_id: daycareId,
                        tueOpen: checkOpen ? openHoursEdit : null,
                        tueClose: checkOpen ? closeHoursEdit : null,
                        tueBreakStart: checkBreak ? breakStartEdit : null,
                        tueBreakEnd: checkBreak ? breakEndEdit : null
                    }
                })
                break
            case t('wednesday'):
                updateDaycare({
                    variables: {
                        daycare_id: daycareId,
                        wedOpen: checkOpen ? openHoursEdit : null,
                        wedClose: checkOpen ? closeHoursEdit : null,
                        wedBreakStart: checkBreak ? breakStartEdit : null,
                        wedBreakEnd: checkBreak ? breakEndEdit : null
                    }
                })
                break
            case t('thursday'):
                updateDaycare({
                    variables: {
                        daycare_id: daycareId,
                        thuOpen: checkOpen ? openHoursEdit : null,
                        thuClose: checkOpen ? closeHoursEdit : null,
                        thuBreakStart: checkBreak ? breakStartEdit : null,
                        thuBreakEnd: checkBreak ? breakEndEdit : null
                    }
                })
                break
            case t('friday'):
                updateDaycare({
                    variables: {
                        daycare_id: daycareId,
                        friOpen: checkOpen ? openHoursEdit : null,
                        friClose: checkOpen ? closeHoursEdit : null,
                        friBreakStart: checkBreak ? breakStartEdit : null,
                        friBreakEnd: checkBreak ? breakEndEdit : null
                    }
                })
                break
            case t('saturday'):
                updateDaycare({
                    variables: {
                        daycare_id: daycareId,
                        satOpen: checkOpen ? openHoursEdit : null,
                        satClose: checkOpen ? closeHoursEdit : null,
                        satBreakStart: checkBreak ? breakStartEdit : null,
                        satBreakEnd: checkBreak ? breakEndEdit : null
                    }
                })
                break
            case t('sunday'):
                updateDaycare({
                    variables: {
                        daycare_id: daycareId,
                        sunOpen: checkOpen ? openHoursEdit : null,
                        sunClose: checkOpen ? closeHoursEdit : null,
                        sunBreakStart: checkBreak ? breakStartEdit : null,
                        sunBreakEnd: checkBreak ? breakEndEdit : null
                    }
                })
                break
            default:
                break
        }
    }

    function handleCheckOpen(): void {
        setCheckOpen(!checkOpen)
        if (!checkOpen) {
            setOpenHoursEdit('08:00')
            setCloseHoursEdit('17:30')
        } else {
            setOpenHoursEdit(null)
            setCloseHoursEdit(null)
        }
    }

    function handleCheckBreak(): void {
        setCheckBreak(!checkBreak)
        if (!checkBreak) {
            setBreakStartEdit('12:00')
            setBreakEndEdit('13:00')
        } else {
            setBreakStartEdit(null)
            setBreakEndEdit(null)
        }
    }

    const disabled: boolean =
        checkOpen === (openHours !== null && closeHours !== null) &&
        checkBreak === (breakStart !== null && breakEnd !== null) &&
        openHoursEdit === openHours &&
        closeHoursEdit === closeHours &&
        breakStartEdit === breakStart &&
        breakEndEdit === breakEnd

    return (
        <Dialog open={open} handleClose={handleClose} className={styles.Dialog}>
            <DialogHeader title={day} />
            <DialogBody className={styles.DialogBody}>
                <div className={styles.InputContainer}>
                    <Checkbox
                        checked={checkOpen}
                        onChange={handleCheckOpen}
                        text={t('open')}
                        className={styles.Checkbox}
                    />
                    {checkOpen && (
                        <>
                            <div className={styles.Input}>
                                <TimePickerComponent
                                    value={openHoursEdit || '08:00'}
                                    setValue={setOpenHoursEdit}
                                    label={t('from')}
                                    minuteToDisable={15}
                                    maxHour={Number(
                                        moment(checkBreak ? breakStartEdit : closeHoursEdit, 'HH:mm')
                                            .subtract('15'.valueOf(), 'minute')
                                            .format('HH')
                                    )}
                                    maxMinute={Number(
                                        moment(checkBreak ? breakStartEdit : closeHoursEdit, 'HH:mm')
                                            .subtract('15'.valueOf(), 'minute')
                                            .format('mm')
                                    )}
                                    className={styles.UserInput}
                                />
                                <TimePickerComponent
                                    value={closeHoursEdit || '17:30'}
                                    setValue={setCloseHoursEdit}
                                    label={t('to')}
                                    minuteToDisable={15}
                                    minHour={Number(
                                        moment(checkBreak ? breakEndEdit : openHoursEdit, 'HH:mm')
                                            .add('15'.valueOf(), 'minute')
                                            .format('HH')
                                    )}
                                    minMinute={Number(
                                        moment(checkBreak ? breakEndEdit : openHoursEdit, 'HH:mm')
                                            .add('15'.valueOf(), 'minute')
                                            .format('mm')
                                    )}
                                    className={styles.UserInput}
                                />
                            </div>
                            <Checkbox checked={checkBreak} onChange={handleCheckBreak} text={t('break')} />
                            {checkBreak && (
                                <div className={styles.Input}>
                                    <TimePickerComponent
                                        value={breakStartEdit || '12:00'}
                                        setValue={setBreakStartEdit}
                                        label={t('from')}
                                        minuteToDisable={15}
                                        minHour={Number(
                                            moment(openHoursEdit, 'HH:mm').add('15'.valueOf(), 'minute').format('HH')
                                        )}
                                        minMinute={Number(
                                            moment(openHoursEdit, 'HH:mm').add('15'.valueOf(), 'minute').format('mm')
                                        )}
                                        maxHour={Number(
                                            moment(breakEndEdit, 'HH:mm')
                                                .subtract('15'.valueOf(), 'minute')
                                                .format('HH')
                                        )}
                                        maxMinute={Number(
                                            moment(breakEndEdit, 'HH:mm')
                                                .subtract('15'.valueOf(), 'minute')
                                                .format('mm')
                                        )}
                                        className={styles.UserInput}
                                    />
                                    <TimePickerComponent
                                        value={breakEndEdit || '13:00'}
                                        setValue={setBreakEndEdit}
                                        label={t('to')}
                                        minuteToDisable={15}
                                        minHour={Number(
                                            moment(breakStartEdit, 'HH:mm').add('15'.valueOf(), 'minute').format('HH')
                                        )}
                                        minMinute={Number(
                                            moment(breakStartEdit, 'HH:mm').add('15'.valueOf(), 'minute').format('mm')
                                        )}
                                        maxHour={Number(
                                            moment(closeHoursEdit, 'HH:mm')
                                                .subtract('15'.valueOf(), 'minute')
                                                .format('HH')
                                        )}
                                        maxMinute={Number(
                                            moment(closeHoursEdit, 'HH:mm')
                                                .subtract('15'.valueOf(), 'minute')
                                                .format('mm')
                                        )}
                                        className={styles.UserInput}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </div>
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon type={'close'} onClick={handleClose} className={styles.Button} />
                    <ButtonTextIcon
                        type={'update'}
                        onClick={updateHoursDaycare}
                        className={styles.Button}
                        disabled={disabled}
                    />
                </div>
            </DialogBody>
        </Dialog>
    )
}

export default EditOpeningHours
